<template>
  <div
    v-editable="blok"
    class="video-player"
    :style="[widthResponsiveStyle]"
    :class="[positionInsideElementClass, videoWidthClass]"
    @mouseenter="hasUserHovered = true"
    @mouseleave="hasUserHovered = false"
  >
    <div
      class="video-container"
      :class="{ 'video-container--interactive': blok.has_controls }"
    >
      <video
        ref="videoRef"
        class="video"
        :class="isMovableClass"
        :style="[
          moveXorPaddingStyle,
          moveYorPaddingStyle,
          borderRadiusStyle,
          borderStyle,
        ]"
        :src="videoSrc"
        :poster="posterSrc"
        autoplay
        loop
        muted
        playsinline
        @timeupdate="handleTimeUpdate"
        @loadedmetadata="handleLoadedMetadata"
        @loadeddata="handleLoadedData"
        @canplay="handleCanPlay"
        @durationchange="handleDurationChange"
        @play="handlePlay"
        @pause="handlePause"
        @volumechange="handleVolumeChange"
        @click="togglePlayPause"
      />

      <!-- Custom Video Controls Overlay -->
      <div v-if="blok.has_controls" v-show="hasUserHovered" class="video-controls-overlay">
        <!-- Progress Bar -->
        <div class="progress-container" @click="seekTo">
          <div class="progress-bar">
            <div
              class="progress-filled"
              :style="{ width: `${progressPercentage}%` }"
            ></div>
          </div>
        </div>

        <!-- Controls Bar -->
        <div class="controls-bar">
          <div class="controls-left">
            <!-- Play/Pause Button -->
            <button
              class="control-btn"
              @click="togglePlayPause"
              :aria-label="isPlaying ? 'Pause video' : 'Play video'"
            >
              <svg
                v-if="isPlaying"
                class="control-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
              <svg
                v-else
                class="control-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>

            <!-- Time Display -->
            <div class="time-display">
              <span class="current-time">{{ formatTime(currentTime) }}</span>
              <span class="time-separator">/</span>
              <span class="total-time">{{ formatTime(duration) }}</span>
            </div>
          </div>

          <div class="controls-right">
            <!-- Volume Control -->
            <div class="volume-container">
              <!-- Volume Slider -->
              <div
                v-show="!isMuted && volume > 0"
                class="volume-slider-container"
              >
                <input
                  type="range"
                  class="volume-slider"
                  min="0"
                  max="1"
                  step="0.1"
                  :value="volume"
                  @input="handleVolumeChange"
                />
              </div>

              <button
                class="control-btn"
                @click="toggleMute"
                :aria-label="isMuted ? 'Unmute video' : 'Mute video'"
              >
                <svg
                  v-if="isMuted || volume === 0"
                  class="control-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                  />
                </svg>
                <svg
                  v-else-if="volume < 0.5"
                  class="control-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"
                  />
                </svg>
                <svg
                  v-else
                  class="control-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                  />
                </svg>
              </button>
            </div>

            <!-- Fullscreen Button -->
            <button
              class="control-btn"
              @click="toggleFullscreen"
              :aria-label="
                isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
              "
            >
              <svg
                v-if="isFullscreen"
                class="control-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                />
              </svg>
              <svg
                v-else
                class="control-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  SbInlineVideoPlayerStoryblok,
  SbPluginResponsiveSpacingNumbers,
} from '@/types'

interface Props {
  blok: SbInlineVideoPlayerStoryblok
  cardPaddingData?: SbPluginResponsiveSpacingNumbers
}

const props = defineProps<Props>()
const videoRef = ref<HTMLVideoElement | null>(null)
const hasUserHovered = ref(false)

// Video state
const isPlaying = ref(false)
const isMuted = ref(true)
const isFullscreen = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const volume = ref(0)

// Computed properties
const progressPercentage = computed(() => {
  if (duration.value === 0) return 0
  return (currentTime.value / duration.value) * 100
})

// Computed properties for video sources
const videoSrc = computed(() => props.blok.video?.filename)
const posterSrc = computed(() => props.blok.poster?.filename)

// Width and positioning classes
const videoWidthClass = computed(() =>
  props.blok.force_width ? 'video-player__width' : 'video-player__max-width',
)

const borderRadiusStyle = computed(() => ({
  '--border-radius-mobile': props.blok.border_radius?.mobile
    ? `${props.blok.border_radius.mobile}px`
    : '0',
  '--border-radius-tablet': props.blok.border_radius?.tablet
    ? `${props.blok.border_radius.tablet}px`
    : '0',
  '--border-radius-desktop': props.blok.border_radius?.desktop
    ? `${props.blok.border_radius.desktop}px`
    : '0',
}))

const widthResponsiveStyle = computed(() => ({
  '--width-mobile': props.blok.max_width_responsive?.mobile
    ? `${props.blok.max_width_responsive.mobile}px`
    : '100%',
  '--width-tablet': props.blok.max_width_responsive?.tablet
    ? `${props.blok.max_width_responsive.tablet}px`
    : '100%',
  '--width-desktop': props.blok.max_width_responsive?.desktop
    ? `${props.blok.max_width_responsive.desktop}px`
    : '100%',
}))

const moveXorPaddingStyle = computed(() => {
  if (props.cardPaddingData) {
    return {
      '--x-mobile': props.blok.move_x?.mobile
        ? `${props.blok.move_x.mobile}px`
        : `${props.cardPaddingData.mobile}px`,
      '--x-tablet': props.blok.move_x?.tablet
        ? `${props.blok.move_x.tablet}px`
        : `${props.cardPaddingData.tablet}px`,
      '--x-desktop': props.blok.move_x?.desktop
        ? `${props.blok.move_x.desktop}px`
        : `${props.cardPaddingData.desktop}px`,
    }
  }
  return {
    '--x-mobile': props.blok.move_x?.mobile
      ? `${props.blok.move_x.mobile}px`
      : '0',
    '--x-tablet': props.blok.move_x?.tablet
      ? `${props.blok.move_x.tablet}px`
      : '0',
    '--x-desktop': props.blok.move_x?.desktop
      ? `${props.blok.move_x.desktop}px`
      : '0',
  }
})

const moveYorPaddingStyle = computed(() => {
  if (props.cardPaddingData) {
    return {
      '--y-mobile': props.blok.move_y?.mobile
        ? `${props.blok.move_y.mobile}px`
        : `${props.cardPaddingData.mobile}px`,
      '--y-tablet': props.blok.move_y?.tablet
        ? `${props.blok.move_y.tablet}px`
        : `${props.cardPaddingData.tablet}px`,
      '--y-desktop': props.blok.move_y?.desktop
        ? `${props.blok.move_y.desktop}px`
        : `${props.cardPaddingData.desktop}px`,
    }
  }
  return {
    '--y-mobile': props.blok.move_y?.mobile
      ? `${props.blok.move_y.mobile}px`
      : '0',
    '--y-tablet': props.blok.move_y?.tablet
      ? `${props.blok.move_y.tablet}px`
      : '0',
    '--y-desktop': props.blok.move_y?.desktop
      ? `${props.blok.move_y.desktop}px`
      : '0',
  }
})

const isMovableClass = computed(() =>
  props.blok.is_movable ? 'video--movable' : '',
)

const positionInsideElementClass = computed(() =>
  props.blok.position?.position
    ? `position__${props.blok.position.position}`
    : '',
)
const bgColor = useState('bgColor')

const borderStyle = computed(() => {
  const color = props.blok.edge_border_color?.value
  if (!color) return {}
  return {
    outline: `2px solid ${color}`,
    outlineOffset: '-2px',
    border: `1px solid ${color}`,
  }
})

// Video control methods
const togglePlayPause = () => {
  if (!videoRef.value || !props.blok.has_controls) return

  if (isPlaying.value) {
    videoRef.value.pause()
  } else {
    videoRef.value.play()
  }
}

const toggleMute = () => {
  if (!videoRef.value) return
  videoRef.value.muted = !videoRef.value.muted
  isMuted.value = videoRef.value.muted

  // If unmuting and volume is 0, set a default volume
  if (!isMuted.value && volume.value === 0) {
    videoRef.value.volume = 0.5
    volume.value = 0.5
  }
}

const toggleFullscreen = () => {
  if (!videoRef.value) return

  if (!isFullscreen.value) {
    if (videoRef.value.requestFullscreen) {
      videoRef.value.requestFullscreen()
    } else if ((videoRef.value as any).webkitRequestFullscreen) {
      ;(videoRef.value as any).webkitRequestFullscreen()
    } else if ((videoRef.value as any).msRequestFullscreen) {
      ;(videoRef.value as any).msRequestFullscreen()
    }
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if ((document as any).webkitExitFullscreen) {
      ;(document as any).webkitExitFullscreen()
    } else if ((document as any).msExitFullscreen) {
      ;(document as any).msExitFullscreen()
    }
  }
}

const seekTo = (event: MouseEvent) => {
  if (!videoRef.value || !duration.value) return

  const progressBar = event.currentTarget as HTMLElement
  const rect = progressBar.getBoundingClientRect()
  const clickX = event.clientX - rect.left
  const percentage = clickX / rect.width
  const newTime = percentage * duration.value

  videoRef.value.currentTime = newTime
}

const handleVolumeChange = (event: Event) => {
  if (!videoRef.value) return

  const target = event.target as HTMLInputElement
  const newVolume = parseFloat(target.value)

  videoRef.value.volume = newVolume
  volume.value = newVolume

  if (newVolume === 0) {
    videoRef.value.muted = true
    isMuted.value = true
  } else if (isMuted.value) {
    videoRef.value.muted = false
    isMuted.value = false
  }
}

// Video event handlers
const handleTimeUpdate = () => {
  if (!videoRef.value) return
  currentTime.value = videoRef.value.currentTime
}

const handleLoadedMetadata = () => {
  if (!videoRef.value) return
  duration.value = videoRef.value.duration
  volume.value = videoRef.value.volume
  isMuted.value = videoRef.value.muted
}

const handlePlay = () => {
  isPlaying.value = true
}

const handlePause = () => {
  isPlaying.value = false
}

const handleLoadedData = () => {
  if (!videoRef.value) return
  duration.value = videoRef.value.duration
  volume.value = videoRef.value.volume
  isMuted.value = videoRef.value.muted
}

const handleCanPlay = () => {
  if (!videoRef.value) return
  duration.value = videoRef.value.duration
  volume.value = videoRef.value.volume
  isMuted.value = videoRef.value.muted
}

const handleDurationChange = () => {
  if (!videoRef.value) return
  duration.value = videoRef.value.duration
}

// Utility functions
const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return '0:00'

  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Fullscreen event listeners
const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement
}

// Watch for when controls appear to ensure video data is captured
watch(hasUserHovered, (newValue) => {
  if (newValue && videoRef.value) {
    // When controls appear, ensure we have the latest video data
    if (videoRef.value.duration && videoRef.value.duration > 0) {
      duration.value = videoRef.value.duration
    }
    if (videoRef.value.currentTime !== undefined) {
      currentTime.value = videoRef.value.currentTime
    }
    if (videoRef.value.volume !== undefined) {
      volume.value = videoRef.value.volume
    }
    if (videoRef.value.muted !== undefined) {
      isMuted.value = videoRef.value.muted
    }
  }
})

// Handle video playback
onMounted(() => {
  if (videoRef.value && props.blok.autoplay) {
    videoRef.value.play().catch((error) => {
      console.warn('Autoplay failed:', error)
    })
  }
  
  // Check if video is already playing (autoplay)
  if (videoRef.value) {
    isPlaying.value = !videoRef.value.paused
  }

  // Set up a timer to check for duration if it's not immediately available
  const checkDuration = () => {
    if (
      videoRef.value &&
      videoRef.value.duration &&
      videoRef.value.duration > 0
    ) {
      duration.value = videoRef.value.duration
      currentTime.value = videoRef.value.currentTime
      volume.value = videoRef.value.volume
      isMuted.value = videoRef.value.muted
    } else {
      // Retry after a short delay
      setTimeout(checkDuration, 100)
    }
  }

  // Start checking for duration immediately
  checkDuration()

  // Also check when the video element is ready
  if (videoRef.value) {
    // If video is already loaded, get the duration immediately
    if (videoRef.value.readyState >= 1) {
      duration.value = videoRef.value.duration
      currentTime.value = videoRef.value.currentTime
      volume.value = videoRef.value.volume
      isMuted.value = videoRef.value.muted
    }
  }

  // Add fullscreen event listeners
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
  document.addEventListener('msfullscreenchange', handleFullscreenChange)
})

onUnmounted(() => {
  // Remove fullscreen event listeners
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
  document.removeEventListener('msfullscreenchange', handleFullscreenChange)
})
</script>

<style lang="scss" scoped>
.video-player {
  --width-mobile: 100%;
  --width-tablet: 100%;
  --width-desktop: 100%;

  display: flex;
  flex-shrink: 0;

  &__width {
    width: var(--width-mobile);

    @media (min-width: $breakpoint-md) {
      width: var(--width-tablet);
    }

    @media (min-width: $breakpoint-lg) {
      width: var(--width-desktop);
    }
  }

  &__max-width {
    max-width: var(--width-mobile);

    @media (min-width: $breakpoint-md) {
      max-width: var(--width-tablet);
    }

    @media (min-width: $breakpoint-lg) {
      max-width: var(--width-desktop);
    }
  }
}

.video-container {
  position: relative;
  cursor: default;

  &--interactive {
    cursor: pointer;
  }
}

.video {
  --x-mobile: 0;
  --x-tablet: 0;
  --x-desktop: 0;
  --y-mobile: 0;
  --y-tablet: 0;
  --y-desktop: 0;
  --border-radius-mobile: 0;
  --border-radius-tablet: 0;
  --border-radius-desktop: 0;

  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: var(--border-radius-mobile);

  @media (min-width: $breakpoint-md) {
    border-radius: var(--border-radius-tablet);
  }

  @media (min-width: $breakpoint-lg) {
    border-radius: var(--border-radius-desktop);
  }

  &--movable {
    transform: translate(var(--x-mobile), var(--y-mobile));

    @media (min-width: $breakpoint-md) {
      transform: translate(var(--x-tablet), var(--y-tablet));
    }

    @media (min-width: $breakpoint-lg) {
      transform: translate(var(--x-desktop), var(--y-desktop));
    }
  }
}

.video-controls-overlay {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  background: rgba(83, 77, 70, 0.55);
  padding: 1rem 1rem 0.5rem;
  border-radius: 0.75rem;
  transition: opacity 0.3s ease-in-out;
  backdrop-filter: blur(15px);
}

.progress-container {
  margin-bottom: 0.5rem;
  cursor: pointer;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  position: relative;
  cursor: pointer;
  transition: height 0.2s ease;

  &:hover {
    height: 10px;
  }
}

.progress-filled {
  height: 100%;
  background: #ffffff;
  border-radius: 2px;
  transition: width 0.1s ease;
}

.controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.controls-left,
.controls-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.control-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid white;
    outline-offset: 2px;
  }
}

.control-icon {
  width: 1.25rem;
  height: 1.25rem;
  fill: currentColor;
}

.time-display {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.time-separator {
  margin: 0 0.25rem;
  opacity: 0.8;
}

.volume-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.volume-slider-container {
  display: flex;
  align-items: center;
}

input[type='range'] {
  transition: all 0.2s ease-in-out;

  -webkit-appearance: none;
  appearance: none;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  height: 6px;
  outline: none;

  &:focus,
  &:focus-visible {
    border: 0 !important;
    appearance: none;
    outline: 1px solid #ffffff;
    outline-offset: 1px;
  }

  &:hover {
    &::-webkit-slider-runnable-track {
      height: 6px;
    }

    &::-moz-range-track {
      height: 6px;
    }

    &::-webkit-slider-thumb {
      border-color: white;
      border-color: white;
      margin-top: -5px;
    }
  }

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 6px;
    cursor: pointer;
    border-radius: 1000px;
  }

  &::-moz-range-track {
    width: 100%;
    height: 6px;
    cursor: pointer;
    border-radius: 1000px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    cursor: pointer;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    margin-top: -5px;
  }

  &::-moz-range-thumb {
    cursor: pointer;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    border: none;
  }
}
.position {
  &__right-top {
    margin-left: auto;
    margin-bottom: auto;
  }

  &__right-bottom {
    margin-left: auto;
    margin-top: auto;
  }

  &__right {
    margin-left: auto;
    margin-top: auto;
    margin-bottom: auto;
  }

  &__left-top {
    margin-right: auto;
    margin-bottom: auto;
  }

  &__left-bottom {
    margin-right: auto;
    margin-top: auto;
  }

  &__left {
    margin-right: auto;
    margin-top: auto;
    margin-bottom: auto;
  }

  &__center {
    margin: auto;
  }

  &__top {
    margin-right: auto;
    margin-left: auto;
    margin-bottom: auto;
  }

  &__bottom {
    margin-right: auto;
    margin-left: auto;
    margin-top: auto;
  }

  &__off {
    margin: 0;
  }
}
</style>
